"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";

export type BookDetailCoverProps = {
  src: string;
  alt: string;
  priority?: boolean;
  className?: string;
};

const DEFAULT_ROTATE_X = 8;
const DEFAULT_ROTATE_Y = -16;
const TILT_RANGE = 18;
const HOVER_LIFT = 12;

export function BookDetailCover({
  src,
  alt,
  priority = false,
  className,
}: BookDetailCoverProps) {
  const tiltRef = useRef<HTMLDivElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  const applyTransform = useCallback(
    (rotateX: number, rotateY: number, lift = 0) => {
      const node = tiltRef.current;
      if (!node || reducedMotion) return;
      node.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(${lift}px)`;
    },
    [reducedMotion],
  );

  const handlePointerEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (reducedMotion) return;

      const node = tiltRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width;
      const py = (event.clientY - rect.top) / rect.height;
      const rotateY = DEFAULT_ROTATE_Y + (px - 0.5) * TILT_RANGE * 2;
      const rotateX = DEFAULT_ROTATE_X + (0.5 - py) * TILT_RANGE * 2;

      applyTransform(rotateX, rotateY, HOVER_LIFT);
    },
    [reducedMotion, applyTransform],
  );

  const handlePointerLeave = useCallback(() => {
    setIsHovering(false);
    applyTransform(DEFAULT_ROTATE_X, DEFAULT_ROTATE_Y, 0);
  }, [applyTransform]);

  return (
    <div
      className={["book-detail-cover-scene", className].filter(Boolean).join(" ")}
    >
      <div
        ref={tiltRef}
        className={[
          "book-detail-cover-book",
          isHovering ? "book-detail-cover-book--hover" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onPointerEnter={handlePointerEnter}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        style={
          reducedMotion
            ? undefined
            : {
                transform: `rotateX(${DEFAULT_ROTATE_X}deg) rotateY(${DEFAULT_ROTATE_Y}deg)`,
              }
        }
      >
        <div className="book-detail-cover-back" aria-hidden="true" />
        <div className="book-detail-cover-pages" aria-hidden="true" />
        <div className="book-detail-cover-pages-top" aria-hidden="true" />

        <div className="book-detail-cover-face">
          <Image
            src={src}
            alt={alt}
            width={560}
            height={840}
            priority={priority}
            sizes="(max-width: 1024px) 90vw, 420px"
            quality={80}
            className="book-detail-cover-image"
          />
          <div className="book-detail-cover-spine" aria-hidden="true" />
          <div className="book-detail-cover-shine" aria-hidden="true" />
          <div className="book-detail-cover-edge-light" aria-hidden="true" />
        </div>
      </div>

      <div className="book-detail-cover-ground" aria-hidden="true" />
    </div>
  );
}
